import { Component, OnInit } from "@angular/core";
import { AppService } from "../app.service";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";

@Component({
    selector: "app-nutrition",
    standalone: false,
    templateUrl: "./nutrition.component.html",
    styleUrls: ["./nutrition.component.css"],
})
export class NutritionComponent implements OnInit {
    foodList: any[] = [];
    apiKey: any;
    userKey: any;
    status: any;
    workspaceName: any;
    loading = true;
    txt: any;

    showErrorMsg: boolean = false;
    showErrorText: any;

    // Server-side pagination
    currentPage: number = 1;
    itemsPerPage: number = 25; // maps to "limit"
    totalEntries: number = 0; // server "total"
    totalPages: number = 0;
    jumpToPageNumber: number = 1;

    // Client-side (current page) search + sort + meal filter
    searchQuery: string = "";
    mealFilter: string = ""; // "" = all
    sortBy: string = "date-desc";
    filteredFood: any[] = [];

    // View mode
    viewMode: "grid" | "list" | "compact" = "grid";

    constructor(
        public appService: AppService,
        public router: Router,
        private http: HttpClient,
        private route: ActivatedRoute
    ) {
        this.userKey = this.route.snapshot.paramMap.get("key");
    }

    ngOnInit(): void {
        const savedViewMode = localStorage.getItem("nutritionViewMode");
        if (savedViewMode) {
            this.viewMode = savedViewMode as "grid" | "list" | "compact";
        }

        const savedItemsPerPage = localStorage.getItem("nutritionItemsPerPage");
        if (savedItemsPerPage) {
            this.itemsPerPage = parseInt(savedItemsPerPage, 10);
        }

        this.listFood();
    }

    async listFood() {
        this.loading = true;
        try {
            const wsJson = this.appService.get("xdbc.workspace");
            if (
                wsJson !== undefined &&
                typeof wsJson === "string" &&
                wsJson.trim() !== "" &&
                wsJson !== "undefined"
            ) {
                try {
                    const ws = JSON.parse(wsJson);
                    this.apiKey = ws.apiKey;
                    this.status = ws.status;
                    this.workspaceName = ws.workspaceName;
                } catch (err) {
                    console.error("Invalid JSON:", err);
                }
            }

            if (this.status === "ACTIVATED") {
                this.showErrorMsg = false;
            } else {
                this.showErrorMsg = true;
                this.showErrorText =
                    "The selected workspace is currently inactive. Please activate it to proceed.";
            }

            const offset = (this.currentPage - 1) * this.itemsPerPage;

            const payload: any = {
                userKey: this.userKey,
                limit: this.itemsPerPage,
                offset: offset,
                routeType: "UI_SESSION",
            };

            const signatureData = await this.appService.wssign(
                payload,
                this.workspaceName
            );

            if (!signatureData) {
                throw new Error("Failed to sign payload");
            }

            const signature = new TextDecoder().decode(signatureData);

            const response = await this.appService.wsPostRequestWithHeaders(
                payload,
                "food/log/list",
                this.apiKey,
                signature
            );

            this.loading = false;

            if (response && response.status == "Success") {
                // Response shape: data.data = entries[], data.total, data.limit, data.offset
                this.foodList = response?.data?.data || [];
                this.totalEntries = response?.data?.total ?? this.foodList.length;
                this.totalPages = Math.ceil(this.totalEntries / this.itemsPerPage);
                this.applyClientView();
                this.txt = response.message;
            } else {
                throw new Error(response?.message);
            }
        } catch (error: any) {
            this.loading = false;
            this.foodList = [];
            this.filteredFood = [];
            this.txt =
                "Failed to load food logs: " + (error.message || "Unknown error");
        }
    }

    // ==================== Client view (search + sort + meal filter on current page) ====================
    applyClientView() {
        let list = [...this.foodList];

        if (this.mealFilter) {
            list = list.filter(
                (f) => (f.mealType || "").toLowerCase() === this.mealFilter
            );
        }

        if (this.searchQuery) {
            const q = this.searchQuery.toLowerCase();
            list = list.filter(
                (f) =>
                    f.foodName?.toLowerCase().includes(q) ||
                    f.description?.toLowerCase().includes(q) ||
                    f.userNotes?.toLowerCase().includes(q) ||
                    (f.dietaryFlags || []).some((t: string) =>
                        t.toLowerCase().includes(q)
                    )
            );
        }

        switch (this.sortBy) {
            case "date-desc":
                list.sort((a, b) => this.entryTime(b) - this.entryTime(a));
                break;
            case "date-asc":
                list.sort((a, b) => this.entryTime(a) - this.entryTime(b));
                break;
            case "calories-desc":
                list.sort((a, b) => (b.calories || 0) - (a.calories || 0));
                break;
            case "calories-asc":
                list.sort((a, b) => (a.calories || 0) - (b.calories || 0));
                break;
            case "health-desc":
                list.sort((a, b) => (b.healthScore || 0) - (a.healthScore || 0));
                break;
        }

        this.filteredFood = list;
    }

    private entryTime(f: any): number {
        if (f.createdAt) return f.createdAt * 1000;
        const dt = `${f.mealDate || ""}T${f.mealTime || "00:00"}`;
        const t = new Date(dt).getTime();
        return isNaN(t) ? 0 : t;
    }

    onSearchChange() {
        this.applyClientView();
    }

    clearSearch() {
        this.searchQuery = "";
        this.applyClientView();
    }

    onMealFilterChange() {
        this.applyClientView();
    }

    onSortChange() {
        this.applyClientView();
    }

    onItemsPerPageChange() {
        localStorage.setItem("nutritionItemsPerPage", this.itemsPerPage.toString());
        this.currentPage = 1;
        this.listFood();
    }

    // ==================== Pagination (server-side) ====================
    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.listFood();
            this.scrollToTop();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.listFood();
            this.scrollToTop();
        }
    }

    goToFirstPage() {
        this.currentPage = 1;
        this.listFood();
        this.scrollToTop();
    }

    goToLastPage() {
        this.currentPage = this.totalPages;
        this.listFood();
        this.scrollToTop();
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages && page !== -1) {
            this.currentPage = page;
            this.listFood();
            this.scrollToTop();
        }
    }

    jumpToPage() {
        if (this.jumpToPageNumber >= 1 && this.jumpToPageNumber <= this.totalPages) {
            this.currentPage = this.jumpToPageNumber;
            this.listFood();
            this.scrollToTop();
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 7;

        if (this.totalPages <= maxPagesToShow) {
            for (let i = 1; i <= this.totalPages; i++) pages.push(i);
        } else {
            if (this.currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push(-1);
                pages.push(this.totalPages);
            } else if (this.currentPage >= this.totalPages - 3) {
                pages.push(1);
                pages.push(-1);
                for (let i = this.totalPages - 4; i <= this.totalPages; i++)
                    pages.push(i);
            } else {
                pages.push(1);
                pages.push(-1);
                for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++)
                    pages.push(i);
                pages.push(-1);
                pages.push(this.totalPages);
            }
        }
        return pages;
    }

    getStartIndex(): number {
        return (this.currentPage - 1) * this.itemsPerPage;
    }

    getEndIndex(): number {
        return Math.min(this.currentPage * this.itemsPerPage, this.totalEntries);
    }

    scrollToTop() {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // ==================== View mode ====================
    setViewMode(mode: "grid" | "list" | "compact") {
        this.viewMode = mode;
        localStorage.setItem("nutritionViewMode", mode);
    }

    // ==================== Macro / nutrition helpers ====================
    protein(f: any): number {
        return Number(f?.macronutrients?.protein_total ?? 0) || 0;
    }

    carbs(f: any): number {
        return Number(f?.macronutrients?.carbs_total ?? 0) || 0;
    }

    fat(f: any): number {
        return Number(f?.macronutrients?.fat_total ?? 0) || 0;
    }

    // Percentage width for the macro bar, by gram weight
    macroPct(f: any, macro: "protein" | "carbs" | "fat"): number {
        const p = this.protein(f);
        const c = this.carbs(f);
        const ft = this.fat(f);
        const total = p + c + ft;
        if (total <= 0) return 0;
        const val = macro === "protein" ? p : macro === "carbs" ? c : ft;
        return Math.round((val / total) * 100);
    }

    // Sum of calories across the current (filtered) page
    pageCalories(): number {
        return this.filteredFood.reduce((sum, f) => sum + (f.calories || 0), 0);
    }

    hasObjData(obj: any): boolean {
        return obj && typeof obj === "object" && Object.keys(obj).length > 0;
    }

    objEntries(obj: any): { key: string; value: any }[] {
        if (!this.hasObjData(obj)) return [];
        return Object.keys(obj).map((k) => ({ key: k, value: obj[k] }));
    }

    prettyLabel(key: string): string {
        return key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    formatNum(value: any, suffix: string = ""): string {
        if (value === null || value === undefined) return "—";
        const num = Number(value);
        if (isNaN(num)) return "—";
        return num.toFixed(num % 1 === 0 ? 0 : 1) + suffix;
    }

    goBack() {
        history.back();
    }
}